import { useId, useState, type InputHTMLAttributes, type ReactNode } from "react";
import { Eye, EyeOff } from "lucide-react";
import { cn } from "../utils/cx.js";
import { Field } from "./Field.js";
import type { InputSize } from "./Input.js";

export interface PasswordInputProps extends Omit<
  InputHTMLAttributes<HTMLInputElement>,
  "size" | "prefix" | "suffix" | "type"
> {
  size?: InputSize;
  prefix?: ReactNode;
  label?: ReactNode;
  help?: ReactNode;
  error?: ReactNode;
  required?: boolean;
}

const HEIGHT = { sm: "h-8", md: "h-9", lg: "h-10" } as const;

/** Password field with a show/hide toggle. Same Field wrapper contract as Input. */
export function PasswordInput({
  size = "md",
  prefix,
  label,
  help,
  error,
  required,
  id,
  className,
  disabled,
  value,
  defaultValue,
  ...rest
}: PasswordInputProps) {
  const auto = useId();
  const inputId = id ?? auto;
  const [visible, setVisible] = useState(false);
  const invalid = error != null;

  const control = (
    <div
      className={cn(
        "flex w-full items-center gap-2 rounded-md border border-input bg-transparent px-3 text-sm shadow-xs outline-none transition-[color,box-shadow] focus-within:border-ring focus-within:ring-ring/50 focus-within:ring-[3px] dark:bg-input/30",
        HEIGHT[size],
        invalid && "border-destructive focus-within:border-destructive focus-within:ring-destructive/30",
        disabled && "opacity-50",
        className,
      )}
    >
      {prefix != null && <span className="shrink-0 text-muted-foreground">{prefix}</span>}
      <input
        id={inputId}
        type={visible ? "text" : "password"}
        disabled={disabled}
        aria-invalid={invalid || undefined}
        className="h-full w-full bg-transparent outline-none placeholder:text-muted-foreground"
        value={value as string | undefined}
        defaultValue={defaultValue as string | undefined}
        {...rest}
      />
      <button
        type="button"
        tabIndex={-1}
        onClick={() => setVisible((v) => !v)}
        disabled={disabled}
        className="shrink-0 text-muted-foreground hover:text-foreground"
        aria-label={visible ? "Gizle" : "Göster"}
      >
        {visible ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
      </button>
    </div>
  );

  if (label != null || help != null || error != null) {
    return (
      <Field label={label} required={required} help={help} error={error} htmlFor={inputId}>
        {control}
      </Field>
    );
  }
  return control;
}
